import { noop, NotificationService, useIntl, type UserEmailVerificationRequirementConfig } from '@capsicum/common';
import { Alert, Snackbar, Stepper } from '@capsicum/common-web';
import { InfoIcon } from '@capsicum/common-web/icons';
import { useEmailVerificationRequirement } from '@capsicum/socios-core';
import { type Requirement } from '@capsicum/socios-core';
import { useEffect } from 'react';
import styled from 'styled-components';

import { EmailCodeVerification } from './EmailCodeVerification';
import { EmailInput } from './EmailInput';

import type { NativeStackNavigationOptions } from '@react-navigation/native-stack';

type EmailVerificationProps = {
	isForcedRequirement?: boolean;
	isUniqueRequirement?: boolean;
	onDismiss: () => void;
	requirement: Requirement<UserEmailVerificationRequirementConfig>;
	setOptions?(options: Partial<NativeStackNavigationOptions>): void;
};

export const EmailVerification = ({
	isForcedRequirement,
	isUniqueRequirement,
	onDismiss,
	requirement
}: EmailVerificationProps) => {
	const { $t } = useIntl();

	const {
		codeVerificationErrorMessage,
		currentStep,
		emailError,
		isSendCodeToEmailLoading,
		isVerifyEmailButtonDisabled,
		onCodeSubmit,
		readonly,
		sendVerificationCodeToEmail,
		setUserEmailHandler,
		stepperRef,
		submitEmailVerificationCodeSuccess,
		updateStep,
		userEmail
	} = useEmailVerificationRequirement(requirement);

	useEffect(() => {
		if (submitEmailVerificationCodeSuccess) {
			NotificationService.addNotification({
				component: (
					<Snackbar
						description={$t('jurisdictionRequirements.emailVerification.successMessage')}
						testID="email-verification-success-notification"
						variant="success"
					/>
				)
			});

			if (isUniqueRequirement) onDismiss();
		}
	}, [submitEmailVerificationCodeSuccess]);

	const EmailInputStep = () => (
		<EmailInput
			disabled={readonly}
			emailError={emailError}
			setUserEmail={setUserEmailHandler}
			userEmail={userEmail}
		/>
	);
	const EmailCodeVerificationStep = () => (
		<EmailCodeVerification
			email={userEmail}
			error={codeVerificationErrorMessage}
			stepperRef={stepperRef}
			onCodeSubmit={onCodeSubmit}
			onResendPress={sendVerificationCodeToEmail}
		/>
	);

	return (
		<S.Wrapper>
			<S.Container>
				<Stepper
					disabledFirstButton={isVerifyEmailButtonDisabled}
					firstButtonText={readonly ? $t('global.button.back') : $t('global.button.verify')}
					fullBorderRadius
					hiddenSecondButton={isForcedRequirement || readonly}
					hideActionButtons={currentStep === 1}
					isLoading={isSendCodeToEmailLoading}
					prerenderSteps={false}
					ref={stepperRef}
					steps={[EmailInputStep, EmailCodeVerificationStep]}
					testID="email-verification-stepper"
					onFirstButtonClick={readonly ? onDismiss : sendVerificationCodeToEmail}
					onSecondButtonClick={onDismiss}
					onStepIndexChange={updateStep}
					onSubmitSteps={noop}
				/>
			</S.Container>
			{currentStep === 1 && (
				<S.AlertContainer>
					<Alert
						closable={false}
						icon={InfoIcon}
						subTitle={$t('auth.welcome.codeInput.info')}
						testID="email-verification-code-info-alert"
						type="message"
					/>
				</S.AlertContainer>
			)}
		</S.Wrapper>
	);
};

namespace S {
	export const Wrapper = styled.div`
		display: flex;
		flex: 1;
		flex-direction: column;
		padding: ${({ theme }) => `${theme.spacings.medium} 0 0`};
		${({ theme }) => theme.breakpoints.queries.below.tabletSmallDesktop} {
			padding: ${({ theme }) => `${theme.spacings.medium} ${theme.spacings.small} 0`};
		}
	`;

	export const Container = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.small};
		${({ theme }) => theme.breakpoints.queries.below.mobile} {
			align-self: center;
			width: 340px;
		}
	`;

	export const AlertContainer = styled.div`
		margin-top: ${({ theme }) => theme.sizing.medium};
	`;
}
